import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import LoadingSpinner from '../../components/LoadingSpinner';
import { CheckCircle, XCircle } from 'lucide-react';

const EmailConfirm = () => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const processConfirmation = async () => {
      try {
        // Get fragment parameters from URL hash
        const hash = window.location.hash.substring(1); // Remove the #
        const params = new URLSearchParams(hash);
        
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const type = params.get('type');

        if (!accessToken) {
          setStatus('error');
          setMessage('Invalid confirmation link.');
          return;
        }

        // Handle different confirmation types
        if (type !== 'signup' && type !== 'invite') {
          setStatus('error');
          setMessage('Invalid confirmation type.');
          return;
        }

        // Call our backend confirmation endpoint
        const response = await fetch(`/api/auth/confirm?access_token=${accessToken}&refresh_token=${refreshToken}&type=${type}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          setStatus('success');
          if (type === 'invite') {
            setMessage('Invitation accepted successfully! You can now log in.');
          } else {
            setMessage('Email confirmed successfully!');
          }
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?confirmed=true');
          }, 3000);
        } else {
          setStatus('error');
          if (type === 'invite') {
            setMessage('Invitation acceptance failed.');
          } else {
            setMessage('Email confirmation failed.');
          }
        }
      } catch (error) {
        console.error('Confirmation error:', error);
        setStatus('error');
        setMessage('An error occurred during confirmation.');
      }
    };

    processConfirmation();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'processing' && (
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <LoadingSpinner size="sm" />
              </div>
            )}
            {status === 'success' && (
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === 'processing' && 'Confirming Email...'}
            {status === 'success' && 'Email Confirmed!'}
            {status === 'error' && 'Confirmation Failed'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center">
          {status === 'processing' && (
            <p className="text-gray-600">
              Please wait while we confirm your email address...
            </p>
          )}
          
          {status === 'success' && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">
                  ✅ {message}
                </AlertDescription>
              </Alert>
              <p className="text-gray-600">
                You will be redirected to the login page in a few seconds...
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  ❌ {message}
                </AlertDescription>
              </Alert>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 rounded-md"
              >
                Go to Login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailConfirm;
